/**
 * Partner Application Card Component
 * Displays the partnership application form for non-partners
 */
import { useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Layers, Plus, Loader2 } from 'lucide-react';
import { applyForPartnership } from '../../lib/api';
import { toast } from 'sonner';
import { motion } from 'framer-motion';

const PartnerApplicationCard = ({ onSuccess }) => {
  const [showApply, setShowApply] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [applyForm, setApplyForm] = useState({
    company_name: '',
    website: '',
    contact_email: '',
    use_case: '',
    expected_volume: '1000-5000'
  });

  const handleApply = async () => {
    if (!applyForm.company_name || !applyForm.website || !applyForm.contact_email) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setSubmitting(true);
    try {
      const res = await applyForPartnership(applyForm);
      setShowApply(false);
      toast.success('Partnership application approved!');
      onSuccess?.(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Application failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <motion.div 
      className="max-w-2xl mx-auto"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      data-testid="partner-apply-page"
    >
      <Card className="text-center py-12">
        <CardContent className="space-y-6">
          <div className="w-16 h-16 mx-auto rounded-full bg-emerald-500/10 flex items-center justify-center">
            <Layers className="w-8 h-8 text-emerald-600" />
          </div>
          <div>
            <h2 className="text-2xl font-heading font-bold">Become a White-label Partner</h2>
            <p className="text-muted-foreground mt-2">
              Embed ParachPay payment solutions into your platform and earn commission on every transaction
            </p>
          </div>
          
          <div className="grid grid-cols-3 gap-4 text-center py-4">
            <div>
              <p className="text-2xl font-bold text-emerald-600">10%</p>
              <p className="text-xs text-muted-foreground">Revenue Share</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-emerald-600">24hr</p>
              <p className="text-xs text-muted-foreground">Approval Time</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-emerald-600">62+</p>
              <p className="text-xs text-muted-foreground">Currencies</p>
            </div>
          </div>
          
          <Dialog open={showApply} onOpenChange={setShowApply}>
            <DialogTrigger asChild>
              <Button size="lg" className="rounded-xl" data-testid="apply-btn">
                <Plus className="w-4 h-4 mr-2" />
                Apply for Partnership
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="font-heading">Partner Application</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Company Name *</Label>
                  <Input
                    value={applyForm.company_name}
                    onChange={(e) => setApplyForm({ ...applyForm, company_name: e.target.value })}
                    placeholder="Your Company"
                    className="rounded-xl"
                    data-testid="company-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Website *</Label>
                  <Input
                    type="url"
                    value={applyForm.website}
                    onChange={(e) => setApplyForm({ ...applyForm, website: e.target.value })}
                    placeholder="https://yourcompany.com"
                    className="rounded-xl"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Contact Email *</Label>
                  <Input
                    type="email"
                    value={applyForm.contact_email}
                    onChange={(e) => setApplyForm({ ...applyForm, contact_email: e.target.value })}
                    placeholder="partner@company.com"
                    className="rounded-xl"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Use Case</Label>
                  <Textarea
                    value={applyForm.use_case}
                    onChange={(e) => setApplyForm({ ...applyForm, use_case: e.target.value })}
                    placeholder="How will you use the white-label API?"
                    className="rounded-xl"
                    rows={3}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Expected Volume</Label>
                  <Select value={applyForm.expected_volume} onValueChange={(v) => setApplyForm({ ...applyForm, expected_volume: v })}>
                    <SelectTrigger className="rounded-xl">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0-1000">0-1,000 transactions/month</SelectItem>
                      <SelectItem value="1000-5000">1,000-5,000 transactions/month</SelectItem>
                      <SelectItem value="5000-20000">5,000-20,000 transactions/month</SelectItem>
                      <SelectItem value="20000+">20,000+ transactions/month</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button onClick={handleApply} disabled={submitting} className="w-full rounded-xl" data-testid="submit-application">
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Submit Application'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default PartnerApplicationCard;
